import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Tabela que vira cartões no celular — a única tabela do /mercado.
 *
 * Com largura sobrando tem a anatomia da `DataTable` (th caixa alta, td 44px,
 * número mono à direita). Quando não cabe, o CSS de `.stack-table` (index.css)
 * esconde o thead e transforma cada `tr` num card em grade de duas colunas: cada
 * `td` escreve o próprio rótulo por `data-label`, a célula `w` ocupa a linha
 * inteira e a `acts` vira a faixa de botões do rodapé do card, com botões de
 * largura igual. **Nada rola de lado, e nada é cortado.**
 *
 * ⚠️ Quem decide o modo é a largura MEDIDA do próprio wrapper (ResizeObserver),
 * não uma media query de viewport. São dois motivos, e os dois já quebraram na
 * tela: a sidebar come 256px, então a janela mente sobre o espaço disponível
 * (numa de 900px sobram 600, e a tabela de 8 colunas era cortada na borda pelo
 * `overflow-x: hidden` do `main`, levando o botão "Recebi" junto); e o limiar é
 * POR TABELA — a de 8 colunas do detalhe do corretor precisa de ~780px, a de 5
 * de A receber cabe em 560, e um limiar único jogaria A receber em cartões numa
 * largura em que ela cabe folgada.
 *
 * É CSS, e não dois componentes (tabela + lista de cards), para que exista UM
 * markup por linha — duas versões da mesma linha divergem no primeiro campo novo.
 */
export interface ColunaStack {
  label: string;
  /** Coluna numérica: cabeçalho alinhado à direita. */
  num?: boolean;
  /** A coluna elástica — a que identifica a linha (mesma marca da célula `w`). */
  w?: boolean;
  className?: string;
}

/**
 * Abaixo desta largura de wrapper, a tabela vira cartões. O default serve às
 * tabelas de até ~5 colunas; quem tem mais colunas passa a sua (ver a prop).
 */
const LARGURA_MINIMA_PADRAO = 560;

export function StackTable({
  colunas,
  children,
  rodape,
  larguraMinima = LARGURA_MINIMA_PADRAO,
  className,
  testId,
}: {
  colunas: ColunaStack[];
  /** As `<tr>` (use `StackRow` + `StackCell`). */
  children: React.ReactNode;
  /** Uma `<tr>` de totais — vira faixa no modo cartão. */
  rodape?: React.ReactNode;
  /**
   * Largura mínima em que ESTA tabela ainda se lê como tabela. Abaixo dela vira
   * cartões. Meça a tabela cheia (com o dado mais largo), não o cabeçalho.
   */
  larguraMinima?: number;
  className?: string;
  testId?: string;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  // Começa em `cards`: é o layout que cabe em qualquer largura, então o primeiro
  // quadro nunca aparece cortado enquanto a medição não chegou.
  const [modo, setModo] = React.useState<"cards" | "tabela">("cards");

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Sem ResizeObserver (jsdom, navegador antigo), fica em cartões — legível em
    // qualquer largura. Nunca o contrário: tabela larga demais seria cortada.
    if (typeof ResizeObserver === "undefined") return;
    const medir = (largura: number) => setModo(largura >= larguraMinima ? "tabela" : "cards");
    medir(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(entradas => {
      for (const e of entradas) medir(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [larguraMinima]);

  return (
    <div ref={ref} className={cn("stack-wrap", className)} data-modo={modo} data-testid={testId}>
      <table className="stack-table">
        <thead>
          <tr>
            {colunas.map(c => (
              <th key={c.label || "acts"} className={cn(c.num && "num", c.w && "w", c.className)}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
        {rodape && <tfoot>{rodape}</tfoot>}
      </table>
    </div>
  );
}

export function StackRow({ children, className, testId }: { children: React.ReactNode; className?: string; testId?: string }) {
  return <tr className={className} data-testid={testId}>{children}</tr>;
}

export interface StackCellProps {
  /** O rótulo que o card escreve no celular. Obrigatório fora de `w`/`acts`. */
  label?: string;
  /** Ocupa a linha inteira do card (a célula que identifica a linha). */
  w?: boolean;
  /** Número: mono, alinhado à direita no desktop. */
  num?: boolean;
  /** Faixa de ações no rodapé do card. */
  acts?: boolean;
  /** Célula sem rótulo no celular (ex.: chip que se explica sozinho). */
  semRotulo?: boolean;
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
}

export function StackCell({ label, w, num, acts, semRotulo, children, className, colSpan }: StackCellProps) {
  return (
    <td
      data-label={label}
      colSpan={colSpan}
      className={cn(w && "w", num && "num", acts && "acts", semRotulo && "sem-rotulo", className)}
    >
      {children}
    </td>
  );
}
